<script>
  import Logo from "@assets/images/altruist-logo.svg"
  import LogoIcon from "@assets/images/logo-icon.svg"
  import Dropdown from '@components/general/dropdown.svelte'
  import Button from '@components/buttons/button.svelte'
  import UpgradeCallout from '@components/general/upgrade-callout.svelte'
  import UserDropdown from '@components/general/user-dropdown.svelte'
  import { page, router } from '@inertiajs/svelte'
  import { FileDown, House, CirclePlay, Video, ExternalLink, BrickWallShield, Menu, User, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-svelte'
  import { IconMessage } from '@tabler/icons-svelte'
  import { cn } from '@lib/utils'
  import { appState } from '@state/app-state.svelte.js'

  let user = $derived($page.props.user)

  let visibilityClass = $derived(appState.sidebarOpen ? 'lg:w-[clamp(11.5rem,23vw,15rem)]' : 'lg:w-12')
  let paddingClass = $derived(appState.sidebarOpen ? 'lg:pl-3 lg:pr-2' : 'lg:px-0')
  let overflowClass = $derived(appState.sidebarOpen ? 'lg:overflow-visible' : 'lg:overflow-hidden')

  let asideClass = $derived(cn(
    "w-full px-4 pt-4 pb-0 sm:px-6 sm:pt-3 lg:pb-2 flex justify-between lg:flex-col lg:justify-start",
    visibilityClass,
    paddingClass,
    overflowClass
  ))

  let desktopNavClass = $derived(cn(
    "hidden lg:flex flex-col gap-2 xl:gap-3 2xl:gap-4 flex-1 min-h-0 overflow-y-auto",
    appState.sidebarOpen ? "lg:opacity-100 lg:translate-x-0" : "lg:opacity-100 lg:translate-x-0 lg:items-center"
  ))

  let logoClass = $derived(cn(
    "block lg:shrink-0 lg:overflow-hidden lg:duration-200 lg:ease-in-out",
    appState.sidebarOpen
      ? "lg:w-[140px] lg:opacity-100 lg:scale-100 lg:mr-3"
      : "lg:w-0 lg:opacity-0 lg:scale-95 lg:pointer-events-none lg:mr-0"
  ))

  let iconLogoClass = $derived(cn(
    "hidden lg:block lg:shrink-0 lg:duration-200 lg:ease-in-out",
    appState.sidebarOpen
      ? "lg:w-0 lg:opacity-0 lg:scale-95 lg:pointer-events-none"
      : "lg:w-7 lg:opacity-100 lg:scale-100"
  ))

  let separatorClass = $derived(cn(
    "border-gray-200 mb-6 lg:duration-200 lg:ease-in-out",
    appState.sidebarOpen ? "lg:opacity-100" : "lg:opacity-0 lg:mb-0"
  ))

  let upperListClass = $derived(cn("flex-1 flex flex-col gap-1", appState.sidebarOpen ? "" : "lg:items-center"))
  
  let links = [
    { path: "/", text: "Dashboard", icon: House },
    { path: "/live-sessions", text: "Live Sessions", icon: Video },
    { path: "/resources", text: "Resources", icon: FileDown },
    { path: "/videos", text: "Videos", icon: CirclePlay },
    { path: "/community", text: "Community", icon: IconMessage },
    { path: "/admin", text: "Admin area", icon: BrickWallShield, admin: true },
    { text: "Altruist website", icon: ExternalLink, externalPath: "https://altruistuk.com" },
  ]

  let navigationLinks = $derived(links.filter(link => !link.admin || user.is_admin))

  // And isn't the membership registration page
  let hasPendingMembership = $derived(user.current_membership?.status === 'inactive')
  let showUpgradeCallout = $derived(!user.is_admin && !user.has_active_membership && !user.has_pending_invoice_request && !hasPendingMembership && !["/memberships/new", "/register"].includes($page.url))

  function itemClass(active, collapsed) {
    return cn(
      "flex w-full items-center gap-3 lg:px-2 lg:py-1.5 xl:px-2.5 border border-solid border-transparent text-gray-600 font-light text-[14px] hover:border-gray-300 hover:bg-white rounded-md focus:outline-none focus:ring-0",
      collapsed ? "lg:w-10 lg:h-10 lg:justify-center lg:gap-0 lg:p-0 xl:p-0" : "",
      active ? "bg-white border-gray-200" : ""
    )
  }

  function handleLogout() {
    if (!window.confirm("Are you sure you want to sign out?")) return
    router.delete('/session')
  }

</script>

{#snippet navigationItem(item, { active, collapsed, hint } = {})}
  <li>
    <Button
      variant="naked"
      onclick={item.onSelect}
      BeforeIcon={item.icon}
      text={collapsed ? "" : item.text}
      hint={hint || (collapsed ? item.text : null)}
      tooltipProps={{ side: "right" }}
      path={item.path}
      externalPath={item.externalPath}
      target={item.target}
      iconProps={collapsed ? { size: 18 } : { size: 16 }}
      class={itemClass(active, collapsed)} />
  </li>
{/snippet}

<!-- In narrow mode the top padding of the main area is sufficient padding, so no bottom padding is required here -->
<aside class={asideClass}>

  <!-- The logo area -->
  <div class={cn("flex items-center h-[40px] lg:h-[78px] w-full", appState.sidebarOpen ? "justify-start" : "justify-start lg:justify-center")}>
    <Button variant="naked" path="/" class={appState.sidebarOpen ? "gap-0" : "gap-0 lg:w-full lg:justify-center"}>
      <img src={Logo} alt="Altruist Wellbeing" width="140" class={logoClass} />
      <img src={LogoIcon} alt="Altruist Wellbeing" width="28" class={iconLogoClass} />
    </Button>
  </div>

  <!-- The separator -->
  <hr class={separatorClass} />

  <!-- The mobile navigation -->
  <Dropdown class="lg:hidden">

    {#snippet trigger({ props })}
      <Button text="Menu" size="sm" Icon={Menu} {...props} />
    {/snippet}

    {#snippet contents(item)}
      {#each navigationLinks as link (link.text)}
        {@const active = $page.url === link.path}
        {@render item({ ...link, selected: active })}
      {/each}
    {/snippet}
  </Dropdown>

  <!-- The desktop navigation -->
  <nav class={desktopNavClass}>

    <!-- The main links -->
    <ul class={upperListClass}>
      {#each navigationLinks as link (link.text)}
        {@render navigationItem(link, { active: $page.url === link.path, collapsed: !appState.sidebarOpen })}
      {/each}

      <!-- <hr class="border-gray-200/70 my-2" /> -->
      
      {#if !appState.sidebarOpen}
        {@render navigationItem({ text: "Your profile", icon: User, path: `/settings/profile` }, { active: $page.url === `/settings/profile`, collapsed: !appState.sidebarOpen })}
        {@render navigationItem({ text: "Sign out", icon: LogOut, onSelect: handleLogout }, { collapsed: !appState.sidebarOpen })}
      {/if}

    </ul>

    {#if appState.sidebarOpen}
      
      <!-- The upgrade callout -->
      {#if showUpgradeCallout}
        <UpgradeCallout class="mt-0" />
      {/if}

    {/if}

    <div class="flex flex-col gap-1">
      <!-- The sidebar toggle button -->
      {@render navigationItem({ 
        text: appState.sidebarOpen ? "Collapse sidebar" : "Expand sidebar", 
        icon: appState.sidebarOpen ? PanelLeftClose : PanelLeftOpen, 
        onSelect: () => appState.sidebarOpen = !appState.sidebarOpen }, 
        { 
          collapsed: !appState.sidebarOpen,
          hint: appState.sidebarOpen ? null : "Expand sidebar (Ctrl+B)"
        }
      )}

      {#if appState.sidebarOpen}
        <!-- The user dropdown -->
        <UserDropdown />
      {/if}
    </div>

  </nav>

</aside>
